import * as React from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { Badge } from "@/components/ui/badge"
import {
  DollarSign,
  PoundSterling,
  AlertTriangle,
  CheckCircle,
  Clock,
  RefreshCw,
  CheckCircle2,
  AlertCircle,
  FileText,
  TrendingUp,
  Sparkles,
} from "lucide-react"
import { KPICard } from "@/components/ar/ar-dashboard/kpi-card"
import { AgingBar } from "@/components/ar/ar-dashboard/aging-bar"
import { ActivityItem } from "@/components/ar/ar-dashboard/activity-item"
import { CustomerRow } from "@/components/ar/ar-dashboard/customer-row"

export function DashboardPage() {
  return (
    <div className="p-6 space-y-6">
      {/* Header Section */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Credit Control Dashboard</h1>
          <p className="text-muted-foreground">
            Real-time overview of outstanding invoices and collection performance
          </p>
        </div>
        <Button variant="outline" size="sm">
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Section 1: Top KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <KPICard
          title="Total Outstanding"
          value="£2,888,000.00"
          subtitle="112 total invoices"
          icon={PoundSterling}
          variant="blue"
        />
        <KPICard
          title="Overdue Invoices"
          value="£1,640,500.00"
          subtitle="56.8% of total"
          icon={AlertTriangle}
          variant="red"
        />
        <KPICard
          title="Collection Rate"
          value="87.4%"
          subtitle="+2.3% vs last month"
          icon={CheckCircle}
          variant="green"
        />
        <KPICard
          title="Avg Days Outstanding"
          value="42.3"
          subtitle="+1.2 days vs target"
          icon={Clock}
          variant="yellow"
        />
      </div>

      {/* Section 2: Middle Grid - Aging Summary & Recent Activities */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Left Card: Aging Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Aging Summary</CardTitle>
            <CardDescription>Outstanding amounts by aging period</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <AgingBar 
              label="Current (0-30)" 
              amount="£1,247,500.00" 
              percentage={43.2} 
              color="green" 
            />
            <AgingBar 
              label="31-60 Days" 
              amount="£687,300.00" 
              percentage={23.8} 
              color="yellow" 
            />
            <AgingBar 
              label="61-90 Days" 
              amount="£456,200.00" 
              percentage={15.8} 
              color="orange" 
            />
            <AgingBar 
              label="91-120 Days" 
              amount="£298,400.00" 
              percentage={10.3} 
              color="lightRed" 
            />
            <AgingBar 
              label="Over 120 Days" 
              amount="£198,600.00" 
              percentage={6.9} 
              color="darkRed" 
            />
          </CardContent>
        </Card>

        {/* Right Card: Recent Activities */}
        <Card>
          <CardHeader>
            <CardTitle>Recent Activities</CardTitle>
            <CardDescription>Latest AR events and updates</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-0">
              <ActivityItem
                icon={CheckCircle2}
                description="Payment received £25,000.00"
                company="Acme Corporation"
                timestamp="8 Mar, 10:30"
                severity="low"
              />
              <ActivityItem
                icon={AlertCircle}
                description="Risk score increased to critical"
                company="Global Industries"
                timestamp="8 Mar, 09:15"
                severity="critical"
              />
              <ActivityItem
                icon={AlertTriangle}
                description="Collection case escalated £89,500.00"
                company="Manufacturing Co"
                timestamp="8 Mar, 08:45"
                severity="high"
              />
              <ActivityItem
                icon={FileText}
                description="New invoice created £15,600.00"
                company="Tech Solutions Inc"
                timestamp="8 Mar, 08:00"
                severity="low"
              />
              <ActivityItem
                icon={CheckCircle2}
                description="Partial payment received £12,500.00"
                company="Retail Partners LLC"
                timestamp="7 Mar, 16:20"
                severity="medium"
                isLast
              />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Section 3: Bottom Grid - Top Customers & AI Insights */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Left Card: Top Customers */}
        <Card>
          <CardHeader>
            <CardTitle>Top Customers</CardTitle>
            <CardDescription>Customers with highest outstanding balances</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-0">
              <CustomerRow
                name="Manufacturing Co"
                outstanding="£567,300.00"
                lastPayment="15 Jan 2024"
                risk={23}
                riskTrend="down"
              />
              <CustomerRow
                name="Global Industries"
                outstanding="£387,200.00"
                lastPayment="22 Feb 2024"
                risk={67}
                riskTrend="up"
              />
              <CustomerRow
                name="Tech Solutions Inc"
                outstanding="£298,450.00"
                lastPayment="5 Mar 2024"
                risk={12}
                riskTrend="down"
              />
              <CustomerRow
                name="Retail Partners LLC"
                outstanding="£245,800.00"
                lastPayment="7 Mar 2024"
                risk={8}
                riskTrend="down"
              />
              <CustomerRow
                name="Acme Corporation"
                outstanding="£189,600.00"
                lastPayment="8 Mar 2024"
                risk={5}
                riskTrend="down"
                isLast
              />
            </div>
          </CardContent>
        </Card>

        {/* Right Card: AI Insights */}
        <Card className="bg-gradient-to-br from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 border-purple-200 dark:border-purple-800">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="rounded-full p-2 bg-purple-100 dark:bg-purple-900/30">
                  <Sparkles className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                </div>
                <CardTitle className="text-purple-900 dark:text-purple-100">AI Insights</CardTitle>
              </div>
              <Badge className="bg-purple-100 text-purple-700 hover:bg-purple-100 dark:bg-purple-900/30 dark:text-purple-300">
                89% confidence
              </Badge>
            </div>
            <CardDescription className="text-purple-700 dark:text-purple-300">
              Machine learning recommendations
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Insight Card 1: Collection Opportunity */}
            <div className="rounded-lg bg-white/80 dark:bg-gray-900/80 p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-3 mb-3">
                <div className="rounded-full p-2 bg-purple-100 dark:bg-purple-900/30">
                  <TrendingUp className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm text-purple-900 dark:text-purple-100 mb-1">
                    Collection Opportunity
                  </h4>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Focus on Manufacturing Co - 89% collection probability
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white dark:bg-purple-700 dark:hover:bg-purple-800">
                  Initiate collection call
                </Button>
                <div className="space-y-1">
                  <div className="flex items-center justify-between text-xs text-purple-700 dark:text-purple-300">
                    <span>Expected recovery</span>
                    <span className="font-semibold">£505,000</span>
                  </div>
                  <Progress value={89} className="h-2 bg-purple-200 dark:bg-purple-900/30 [&>div]:bg-purple-600 dark:[&>div]:bg-purple-500" />
                </div>
              </div>
            </div>

            {/* Insight Card 2: Risk Alert */}
            <div className="rounded-lg bg-white/80 dark:bg-gray-900/80 p-4 border border-purple-200 dark:border-purple-800">
              <div className="flex items-start gap-3">
                <div className="rounded-full p-2 bg-amber-100 dark:bg-amber-900/30">
                  <AlertCircle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm text-purple-900 dark:text-purple-100 mb-1">
                    Risk Alert
                  </h4>
                  <p className="text-sm text-purple-700 dark:text-purple-300">
                    Global Industries showing payment pattern deterioration
                  </p>
                  <Button variant="link" className="h-auto p-0 text-xs text-purple-600 dark:text-purple-400 mt-1">
                    View risk analysis →
                  </Button>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
